
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Product } from "@/types/product";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Link, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface ProductFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: ProductFormData) => Promise<void>;
  editingProduct: Product | null;
}

export interface ProductFormData {
  name: string;
  description: string;
  price: string;
  image_url: string;
  category: string;
}

const ProductForm = ({ isOpen, onClose, onSubmit, editingProduct }: ProductFormProps) => {
  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    description: "",
    price: "",
    image_url: "",
    category: ""
  });
  const [useUrlInput, setUseUrlInput] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (editingProduct) {
        setFormData({
          name: editingProduct.name || "",
          description: editingProduct.description || "",
          price: editingProduct.price.toString() || "",
          image_url: editingProduct.image_url || "",
          category: editingProduct.category || ""
        });
        setPreviewUrl(editingProduct.image_url || null);
        setUseUrlInput(true);
      } else {
        setFormData({
          name: "",
          description: "",
          price: "",
          image_url: "",
          category: ""
        });
        setPreviewUrl(null);
        setUseUrlInput(true);
      }
    }
  }, [isOpen, editingProduct]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Arquivo inválido",
        description: "Por favor, selecione uma imagem (JPG, PNG, WEBP, etc.)",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "A imagem deve ter no máximo 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `product-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("products")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("products")
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      setPreviewUrl(publicUrl);
      toast({
        title: "Imagem enviada",
        description: "A imagem foi carregada com sucesso!",
      });
    } catch (error: any) {
      console.error("Erro ao enviar imagem:", error);
      toast({
        title: "Erro ao enviar imagem",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingProduct ? "Editar Produto" : "Adicionar Produto"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-white">Nome do Produto *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              required
            />
          </div>
          <div>
            <Label htmlFor="description" className="text-white">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              rows={3}
            />
          </div>
          <div>
            <Label htmlFor="price" className="text-white">Preço (R$) *</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              required
            />
          </div>
          <div>
            <Label htmlFor="category" className="text-white">Categoria *</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Ex: Lanches, Bebidas, Porções"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-white">Imagem do Produto</Label>
              <div className="flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant={!useUrlInput ? "default" : "outline"}
                  onClick={() => setUseUrlInput(false)}
                  className={!useUrlInput ? "bg-krecao-red hover:bg-krecao-red/90 h-7 text-xs" : "border-gray-700 hover:bg-gray-800 text-white h-7 text-xs"}
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Enviar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={useUrlInput ? "default" : "outline"}
                  onClick={() => setUseUrlInput(true)}
                  className={useUrlInput ? "bg-krecao-red hover:bg-krecao-red/90 h-7 text-xs" : "border-gray-700 hover:bg-gray-800 text-white h-7 text-xs"}
                >
                  <Link className="h-3 w-3 mr-1" />
                  URL
                </Button>
              </div>
            </div>

            {useUrlInput ? (
              <Input
                id="image_url"
                type="url"
                value={formData.image_url}
                onChange={(e) => {
                  setFormData({ ...formData, image_url: e.target.value });
                  setPreviewUrl(e.target.value || null);
                }}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="https://exemplo.com/imagem.jpg"
              />
            ) : (
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-700 rounded-md p-4 text-center cursor-pointer hover:border-gray-500 transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Enviando...</span>
                  </div>
                ) : (
                  <div className="text-gray-400">
                    <Upload className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-sm">Clique para selecionar uma imagem</p>
                    <p className="text-xs mt-1">JPG, PNG ou WEBP (máx. 5MB)</p>
                  </div>
                )}
              </div>
            )}

            {previewUrl && (
              <div className="mt-2 rounded-md overflow-hidden border border-gray-700">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-32 object-cover"
                  onError={() => setPreviewUrl(null)}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-700 hover:bg-gray-800 text-white"
            >
              Cancelar
            </Button>
            <Button type="submit" className="bg-krecao-red hover:bg-krecao-red/90" disabled={uploading}>
              {editingProduct ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductForm;
